import { useEffect, useRef, useState } from 'react';
import { YjsProvider } from '@/collaboration/yjs-provider';
import type { RemoteCursor, AwarenessUser } from '@/collaboration/yjs-provider';
import { getSocket } from '@/collaboration/socket';
import { useCanvasStore } from '@/stores/canvas.store';
import { useAuthStore } from '@/stores/auth.store';

// Deterministic color palette for remote users
const USER_COLORS = [
  '#EF4444', // red
  '#F59E0B', // amber
  '#10B981', // emerald
  '#3B82F6', // blue
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#14B8A6', // teal
  '#F97316', // orange
];

function colorForUser(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length];
}

export interface UseCollaborationReturn {
  isConnected: boolean;
  connectedUsers: AwarenessUser[];
  remoteCursors: RemoteCursor[];
}

/**
 * React hook that bridges Yjs collaboration with the Zustand canvas store.
 * Should be called once inside the canvas page component.
 */
export function useCollaboration(canvasId: string | undefined): UseCollaborationReturn {
  const providerRef = useRef<YjsProvider | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [remoteCursors, setRemoteCursors] = useState<RemoteCursor[]>([]);
  const [connectedUsers, setConnectedUsers] = useState<AwarenessUser[]>([]);

  // Refs to avoid stale closures when syncing Zustand → Yjs
  const isSyncingFromRemote = useRef(false);

  const nodes = useCanvasStore((s) => s.nodes);
  const edges = useCanvasStore((s) => s.edges);
  const user = useAuthStore((s) => s.user);

  // ---- Provider lifecycle ----

  useEffect(() => {
    if (!canvasId) return;

    const provider = new YjsProvider(canvasId);
    providerRef.current = provider;

    // Listen for remote node/edge changes → push into Zustand
    const unsubNodes = provider.onNodesChange((remoteNodes) => {
      isSyncingFromRemote.current = true;
      useCanvasStore.setState({ nodes: remoteNodes });
      isSyncingFromRemote.current = false;
    });

    const unsubEdges = provider.onEdgesChange((remoteEdges) => {
      isSyncingFromRemote.current = true;
      useCanvasStore.setState({ edges: remoteEdges });
      isSyncingFromRemote.current = false;
    });

    // Listen for remote cursors
    const unsubCursors = provider.onRemoteCursors((cursors) => {
      setRemoteCursors(cursors);
      // Derive connected users from cursor data
      const usersMap = new Map<string, AwarenessUser>();
      for (const c of cursors) {
        usersMap.set(c.user.id, c.user);
      }
      setConnectedUsers(Array.from(usersMap.values()));
    });

    // Socket connection state
    const socket = getSocket();

    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    setIsConnected(socket.connected);

    return () => {
      unsubNodes();
      unsubEdges();
      unsubCursors();
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      provider.destroy();
      providerRef.current = null;
    };
  }, [canvasId]);

  // ---- Sync Zustand → Yjs (local changes only) ----

  useEffect(() => {
    if (!providerRef.current) return;
    if (isSyncingFromRemote.current) return;
    providerRef.current.setNodes(nodes);
  }, [nodes]);

  useEffect(() => {
    if (!providerRef.current) return;
    if (isSyncingFromRemote.current) return;
    providerRef.current.setEdges(edges);
  }, [edges]);

  // ---- Awareness: track mouse position ----

  useEffect(() => {
    if (!canvasId || !user) return;

    const provider = providerRef.current;
    if (!provider) return;

    const awarenessUser: AwarenessUser = {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      color: colorForUser(user.id),
    };

    const handleMouseMove = (e: MouseEvent) => {
      provider.setLocalCursor({ x: e.clientX, y: e.clientY }, awarenessUser);
    };

    // Throttle to ~20fps to avoid flooding the server
    let lastSent = 0;
    const throttledMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastSent < 50) return;
      lastSent = now;
      handleMouseMove(e);
    };

    window.addEventListener('mousemove', throttledMove);

    return () => {
      window.removeEventListener('mousemove', throttledMove);
    };
  }, [canvasId, user]);

  return {
    isConnected,
    connectedUsers,
    remoteCursors,
  };
}

export default useCollaboration;
